import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Spacer from "@ui/spacer/Spacer";
import { Typo } from "@ui/text/Typo";
import { Shield } from "lucide-react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMobile } from "src/hooks/useMobile";
import { Inner } from "src/services/Router";
import { GD } from "../GD";
import { AUTH_STATE } from "../services/AuthService";
import { AuthView } from "./Auth/AuthView";
import { Breadcrumbs } from "./Breadcurmbs";
import { ClientNavigation } from "./pages/clientPage/ClientNavigation";
import { LeftMenu, ProjectSelector } from "./LeftMenu";
import { ProfileMenu } from "./ProfileMenu";
import { LivenessStatus } from "./liveness/LivenessStatus";
import { ProjectEditorView } from "./ProjectEditorView";
import { SelectProjectView } from "./SelectProjectView";
import { TransactionPreview } from "./TransactionPreview";
import { AcquirerEditor } from "./acquirerEditor/AcquirerEditor";
import { ChangeSessionStatusPopup } from "./conponents/popup/ChangeSessionStatusPopup";
import { ClientRegisterPopup } from "./conponents/popup/ClientRegisterPopup";
import { DectaSessionPopup } from "./conponents/popup/DectaSessionPopup";
import { ListValuesPopup } from "./conponents/popup/ListValuesPopup";
import { RawTextPopup } from "./conponents/popup/RawTextPopup";
import { RefundPopup } from "./conponents/popup/RefundPopup";
import { WebhookDetailPopup } from "./conponents/popup/WebhookDetailPopup";
import { WebsiteRequestPopup } from "./conponents/popup/WebsiteRequestPopup";
import { S_FRAUD_GROUPS_POPUP } from "./fraud/FraudGroupsPopup";
import { FraudPopupContainer } from "./fraud/FraudPopupContainer";
import { SelectIntegrationPopup } from "./pages/IntegrationsList";
import { AcquirerPopup } from "./popup/AcquirerPopup";
import { AddBankPopup } from "./popup/AddBankPopup";
import { AddChannelPopup } from "./popup/AddChannelPopup";
import { AddClientPopup } from "./popup/AddClientPopup";
import { AddCommentPopup } from "./popup/AddCommentPopup";
import { AddFilesPopup } from "./popup/AddFilesPopup";
import { AddGroupPopup } from "./popup/AddGroupPopup";
import { AddIbanPopup } from "./popup/AddIbanPopup";
import { AddMerchantChannelPopup } from "./popup/AddMerchantChannelPopup";
import { AddStatusPopup } from "./popup/AddStatusPopup";
import { AddTerminalPopup } from "./popup/AddTerminalPopup";
import { ClientCredentialsPopup } from "./popup/ClientCredentialsPopup";
import { ClientSettingsPopup } from "./popup/ClientSettingsPopup";
import { ClientStatusChangePopup } from "./popup/ClientStatusChangePopup";
import { ClientStatusPopup } from "./popup/ClientStatusPopup";
import { DectaFilesPopup } from "./popup/DectaFilesPopup";
import { FeeComparePopup, S_FEE_COMPARE_POPUP } from "./popup/FeeComparePopup";
import { FeePopup } from "./popup/FeePopup";
import { InviteUserPopup } from "./popup/InviteUserPopup";
import { ApiUserPopup } from "./popup/ApiUserPopup";
import { MerchantControlPopup } from "./popup/MerchantControlPopup";
import { MerchantTerminalPopup } from "./popup/MerchantTerminalPopup";
import { ProfilePopup } from "./popup/ProfilePopup";
import { SelectCountriesPopup } from "./popup/SelectCountriesPopup";
import { SelectItemsPopup } from "./popup/SelectItemsPopup";
import { SettingsPopup } from "./popup/SettingsPopup";
import { TestPaymentPopup } from "./popup/TestPaymentPopup";
import { S_TRANSACTION_FEE_POPUP, TransactionFeePopup } from "./popup/TransactionFee";
import { UserEditor } from "./popup/UserEditor";
import { ConfirmDialog } from "./popup/general/ConfirmDialog";
import { RegisterPayswixClientPopup } from "./popup/RegisterPayswixClientPopup";
import { ProvidersPopupContainer } from "./providers/ProvidersPopupContainer";
import { ReportPopup } from "./reports/ReportPopup";
import { StatementPopup } from "./reports/StatementPopup";
import { AcquirerChannelEditor } from "./routeEditor/AcquirerChannelEditor";
import { AttachAcquirerPopup } from "./terminalEditor/AttachAcquirerPopup";
import { TerminalEditor } from "./terminalEditor/TerminalEditor";
import { BarChartDetailsPopup } from "./statView/BarChartDetailsPopup";
import { BarChartSectionalDetailsPopup } from "./statView/BarChartSectionalDetailsPopup";
import { hasPermission, hasRole } from "src/permissions";

const UserAccessNotice = () => { useLocale(); return ((
	<VBox
		sx={{
			minHeight: "calc(100vh - 110px)",
			alignItems: "center",
			justifyContent: "center",
			padding: "48px 24px",
			textAlign: "center",
		}}>
		<Box
			sx={{
				width: "48px",
				height: "48px",
				borderRadius: "50%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "hsl(30 20% 94%)",
				color: "hsl(17 35% 48%)",
				marginBottom: "16px",
			}}>
			<Shield size={22} strokeWidth={1.7} />
		</Box>
		<Typo sx={{ fontSize: "18px", fontWeight: 600, color: "hsl(0 1% 30%)", marginBottom: "8px" }}>{msg("noWorkspaceAccessYet")}</Typo>
		<Typo sx={{ maxWidth: "440px", fontSize: "14px", lineHeight: "1.55", color: "hsl(0 1% 52%)" }}>{msg("yourCurrentRoleDoesnTIncludeAccessToAnySectionsAvailableArea_58d41e8d")}</Typo>
	</VBox>
)); };

// memo: GlobalView takes no props, so wrapping it stops App's frequent re-renders from
// cascading into GlobalView and its ~50 popup containers. GlobalView still re-renders on
// its own signal subscriptions (auth state, project, profile) when those actually change.
export const GlobalView = memo(() => {
	const state = GD.S_AUTH_STATUS.use([]);

	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);

	const [showLeftMenu, setShowLeftMenu] = useState(false);
	const [showProjectInHeader, setShowProjectInHeader] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const { isMobile, isTablet } = useMobile();

	const headerElRef = useRef<HTMLDivElement | null>(null);
	const roRef = useRef<ResizeObserver | null>(null);

	useEffect(() => {
		GD.B_HEADER_HEIGHT.onRequest(async () => {
			const el = headerElRef.current;
			if (el) GD.B_HEADER_HEIGHT.invoke(el.getBoundingClientRect().height);
		});
	}, []);

	const headerRef = useCallback((el: HTMLDivElement | null) => {
		headerElRef.current = el;
		if (roRef.current) {
			roRef.current.disconnect();
			roRef.current = null;
		}
		if (!el) return;
		const ro = new ResizeObserver(() => {
			GD.B_HEADER_HEIGHT.invoke(el.getBoundingClientRect().height);
		});
		ro.observe(el);
		roRef.current = ro;
	}, []);

	useEffect(() => {
		if (project && user) {
			const userProject = user.projects.find(val => val.id === project.id);
			if (userProject) {
				if (hasPermission(userProject.roles, "layout.sidebar")) {
					setShowLeftMenu(true);
					setShowProjectInHeader(false);
				} else if (hasRole(userProject.roles, "merchant")) {
					setShowLeftMenu(false);
					setShowProjectInHeader(true);
				} else {
					setShowLeftMenu(false);
					setShowProjectInHeader(false);
				}
			}
		}
	}, [project, user]);

	const currentRoles = project && user ? user.projects.find(val => val.id === project.id)?.roles : undefined;
	const showAdminControls: boolean = project?.id !== 3 && hasPermission(currentRoles, "layout.financial_tools");
	const showUserAccessNotice = currentRoles?.length === 1 && hasRole(currentRoles, "user");
	const showUserProjectSelector = showUserAccessNotice && (user?.projects.length ?? 0) > 1;

	// if (project && user) {
	// 	const userProject = user.projects.find(val => val.id === project.id);
	// 	if (userProject) {
	// 		if(project.id !== 3)
	// 		{
	// 			showAdminControls = true;
	// 		}
	// 		// const userRoles = userProject.roles.map(r => r.name);
	// 		// if (userRoles.includes("admin")) {
	// 		// 	showAdminControls = true;
	// 		// }
	// 	}
	// }

	if (window.location.pathname.indexOf("restore/") !== -1) {
		return <AuthView />;
	}
	if (window.location.pathname.indexOf("invite/") !== -1) {
		return <AuthView />;
	}

	if (state === AUTH_STATE.UNAUTHORIZED) {
		return <AuthView />;
	}

	if (state === AUTH_STATE.SELECT_PROJECT) {
		return <SelectProjectView />;
	}

	if (state === null) {
		return null;
	}

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: isMobile || !showLeftMenu ? "minmax(0, 1fr)" : "max-content minmax(0, 1fr)",
				width: "100%",
				minHeight: "100vh",
				backgroundColor: "hsl(40 18% 97%)",
			}}>
			{/* Sidebar — desktop: sticky column, mobile: overlay */}
			{!isMobile && showLeftMenu && (
				<Box
					sx={{
						position: "sticky",
						top: "0",
						height: "100vh",
						zIndex: 30,
					}}>
					<LeftMenu />
				</Box>
			)}
			{/* Mobile menu overlay */}
			{isMobile && showLeftMenu && (
				<>
					<Box
						onClick={() => setMobileMenuOpen(false)}
						sx={{
							position: "fixed",
							inset: "0",
							backgroundColor: mobileMenuOpen ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0)",
							zIndex: 100,
							transition: "background-color 0.3s ease",
							pointerEvents: mobileMenuOpen ? "auto" : "none",
						}}
					/>
					<Box
						sx={{
							position: "fixed",
							top: "0",
							left: "0",
							bottom: "0",
							width: "280px",
							backgroundColor: "hsl(40 10% 95%)",
							zIndex: 101,
							boxShadow: mobileMenuOpen ? "4px 0 24px rgba(0,0,0,0.15)" : "none",
							overflowY: "auto",
							transform: mobileMenuOpen ? "translateX(0)" : "translateX(-100%)",
							transition: "transform 0.3s ease, box-shadow 0.3s ease",
						}}>
						<LeftMenu onItemClick={() => setMobileMenuOpen(false)} />
					</Box>
				</>
			)}
			<VBox
				sx={{
					paddingRight: isMobile ? "0px" : "0px",
					paddingBottom: "0px",
					paddingLeft: "0px",
					minWidth: 0,
					backgroundColor: "hsl(40 18% 97%)",
				}}>
				<Box
					ref={headerRef}
					sx={{
						backgroundColor: "hsl(40 18% 97%)",
						top: "0px",
						position: "sticky",
						zIndex: 20,
					}}>
					<Box
						sx={{
							position: "absolute",
							left: isMobile ? "12px" : "20px",
							right: isMobile ? "12px" : "20px",
							bottom: "0",
							height: "1px",
							backgroundColor: "#f2ebe7",
							pointerEvents: "none",
						}}
					/>
					<HBox
						center
						sx={{
							minHeight: "59px",
							padding: isMobile ? "0 12px" : "0 20px",
							flexWrap: isTablet ? "wrap" : "nowrap",
							gap: isTablet ? "6px" : "0px",
						}}>
						{isMobile && showLeftMenu && (
							<>
								<Box
									onClick={() => setMobileMenuOpen(prev => !prev)}
									sx={{
										cursor: "pointer",
										padding: "6px",
										borderRadius: "6px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										"&:hover": { backgroundColor: "hsl(40 10% 91%)" },
									}}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none">
										<path d="M3 6h18M3 12h18M3 18h18" stroke="hsl(0 1% 37%)" strokeWidth="2" strokeLinecap="round" />
									</svg>
								</Box>
								<Spacer size="4px" />
							</>
						)}

						{showUserProjectSelector ? <ProjectSelector small={false} /> : !showUserAccessNotice && <Breadcrumbs showProject={showProjectInHeader} />}
						<Spacer grow />
						{!isTablet && showAdminControls && (
							<>
								<Button variant="secondarySmall" onClick={() => S_FRAUD_GROUPS_POPUP.invoke({})}>{msg("fraudRules")}</Button>
								<Spacer size="8px" />
								<Button variant="secondarySmall" onClick={() => S_FEE_COMPARE_POPUP.invoke({})}>{msg("feeCompare")}</Button>
								<Spacer size="8px" />
								<Button variant="secondarySmall" onClick={() => S_TRANSACTION_FEE_POPUP.invoke({})}>{msg("feeIc")}</Button>
								<Spacer size="20px" />
							</>
						)}
						{!showUserAccessNotice && <ClientNavigation />}
						<LivenessStatus key={project?.id} />
						{!showLeftMenu && <ProfileMenu collapsed={isMobile} placement="bottom" />}
					</HBox>
				</Box>
				<VBox
					sx={{
						flexGrow: "1",
						overflowX: "clip",
						position: "relative",
						minWidth: 0,
						padding: isMobile ? "10px 8px" : "14px 20px 20px",
						backgroundColor: "hsl(40 18% 97%)",
					}}>
					{showUserAccessNotice ? <UserAccessNotice /> : <Inner />}
				</VBox>
			</VBox>
			<ProjectEditorView />
			<AcquirerEditor />
			<AcquirerChannelEditor />
			<TerminalEditor />
			<UserEditor />
			<AttachAcquirerPopup />
			<AddStatusPopup />
			<AddBankPopup />
			<AddIbanPopup />
			<ClientStatusPopup />
			<AddChannelPopup />
			<RegisterPayswixClientPopup />
			<AddClientPopup />
			<AddFilesPopup />
			<AddCommentPopup />
			<AddMerchantChannelPopup />
			<MerchantTerminalPopup />
			<AddGroupPopup />
			<ClientStatusChangePopup />
			<StatementPopup />
			<ReportPopup />
			<ClientSettingsPopup />
			<ClientCredentialsPopup />
			<ProvidersPopupContainer /> {/* POP UP CONTAINERS FOR PAYMENT PROVIDERS*/}
			<MerchantControlPopup />
			<AcquirerPopup />
			<AddTerminalPopup />
			<SelectIntegrationPopup />
			<InviteUserPopup />
			<ApiUserPopup />
			<TransactionPreview />
			<SettingsPopup /> {/** назвать надо как ProjectSettingsPopup */}
			<TestPaymentPopup />
			<FeePopup />
			<FraudPopupContainer />
			<FeeComparePopup />
			<TransactionFeePopup />
			<DectaFilesPopup />
			<ProfilePopup />
			<WebsiteRequestPopup />
			<ClientRegisterPopup />
			{/* Auxiliary dialogs must sit above editors portaled to body (Panel uses z-index: 1000). */}
			{createPortal(
				<div style={{ position: "relative", zIndex: 1001 }}>
					<DectaSessionPopup />
					<RefundPopup />
					<WebhookDetailPopup />
					<ChangeSessionStatusPopup />
					<RawTextPopup />
					<ListValuesPopup />
					<SelectCountriesPopup />
					<SelectItemsPopup />
					<BarChartDetailsPopup />
					<BarChartSectionalDetailsPopup />
					<ConfirmDialog />
				</div>,
				document.body,
			)}
		</Box>
	);
});
GlobalView.displayName = "GlobalView";

// !! Соблюдай иерархию попапов. нельзя ничего ставить выше confirm-a и так далее
